import { Component, OnInit, ViewChild, ElementRef, OnChanges } from '@angular/core';
import * as d3 from 'd3';
import { HierarchyNode } from 'd3';

import { Partition } from '@classes/simulation/partition';
import { UiService } from '@services/ui.service';
import { PartitionsSunburstBase } from './partitions-sunburst-base/partitions-sunburst-base';

@Component({
    selector: 'app-partitions-sunburst',
    templateUrl: './partitions-sunburst.component.html',
    styleUrls: ['./partitions-sunburst.component.scss']
})
export class PartitionsSunburstComponent extends PartitionsSunburstBase implements OnInit, OnChanges {
    private chartContainer: ElementRef;

    @ViewChild('sunburst', { static: true }) set content(content: ElementRef) {
        this.chartContainer = content;
    }

    colors: string[];
    colorsTree: {
        code: string,
        color: string
    }[];

    vis: any;

    isDetailsVisible: boolean;
    showedLabel: string;
    showedPercent: string;
    showedValue: number;
    selectedNodes: Partition[];

    constructor(
        private uiSrv: UiService
    ) {
        super();
    }

    ngOnInit() {
        // this.initChart();
    }

    ngOnChanges() {
        this.initChart();
    }

    private initChart(): void {
        this.cleanUpChart();

        this.colors = this.uiSrv.getChartsColors();
        this.colorsTree = [];

        if (this.mainPartition) {
            this.setPartsColors(this.mainPartition.partitions);
            this.drawSunburst();
            this.selectPart();
        }
    }

    // create sunburst chart objects with D3.js
    private drawSunburst(): void {
        const json: Partition = JSON.parse(JSON.stringify(this.mainPartition));
        const element = this.chartContainer.nativeElement;
        const width = this.chartSize || 250;
        const height = width;
        const radius = width / 2;

        this.vis = d3.select(element).append("svg:svg")
            .attr("width", width)
            .attr("height", height)
            .append("svg:g")
            .attr("id", `container`)
            .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

        // Bounding circle underneath the sunburst, to make it easier to detect
        // when the mouse leaves the parent g.
        this.vis.append("svg:circle")
            .attr("r", radius)
            .style("opacity", 0);

        const partition = d3.partition()
            .size([2 * Math.PI, radius * radius]);

        const arc = d3.arc()
            .startAngle((d: any) => d.x0)
            .endAngle((d: any) => d.x1)
            .innerRadius((d: any) => Math.sqrt(d.y0))
            .outerRadius((d: any) => Math.sqrt(d.y1));

        const root = d3.hierarchy(json, d => d.partitions)
            .sum(d => d.partitions.length ? 0 : d.ratio);

        const nodes = partition(root).descendants();

        this.vis.data([json]).selectAll("path")
            .data(nodes)
            .enter().append("svg:path")
            .attr("display", d => d.depth && d.data.isVisible ? null : "none")
            .attr("d", arc)
            .attr("fill-rule", "inherit")
            .style("fill", d => this.isGrey ? '#51626f' : this.fillColor(d))
            .style('stroke', '#FFF')
            .style("opacity", 1)
            .on("mouseover", d => this.highlightPath(d))
            .on("mouseleave", () => this.mouseleave());

    }

    // Set the color of each node
    private fillColor(d: HierarchyNode<Partition>) {
        const found = this.colorsTree.find(color => color.code === d.data.partitionDetails.code);
        if (found) {
            return found.color;
        }

        return '#51626f';
    }

    private cleanUpChart() {
        const element = this.chartContainer.nativeElement;
        d3.select(element.querySelector('svg')).remove();
    }

    private mouseleave(): void {
        this.clearHighlight();
        this.selectPart();
    }

    // On selection from partitions tree
    // Manage single/multi selection
    private selectPart(): void {
        if (this.selected) {
            this.selectSinglePart();
        } else if (this.selectedCodes && this.selectedCodes.length) {
            this.selectMultipleParts();
        } else {
            this.clearHighlight();
        }
    }

    private selectSinglePart(): void {
        // Get corresponding node from selected partition
        this.vis
            .selectAll("path")
            .filter((node) => {
                if (node.data.partitionDetails.code === this.selected.partitionDetails.code) {
                    if (node.depth > 0) {
                        this.highlightPath(node);
                    } else {
                        this.clearHighlight();
                    }
                }
            });
    }

    private selectMultipleParts(): void {
        // // Fade all the segments.
        this.vis.selectAll("path").style("opacity", 0.3);
        this.selectedNodes = [];
        this.isDetailsVisible = false;

        this.selectedCodes.map((code, index) => {
            // Then highlight only those that are an ancestor of the current segment.
            this.vis.selectAll("path")
                .filter(node => {
                    if (node.data.partitionDetails.code === code && node.depth > 0) {
                        this.selectedNodes.push(node.data);
                    }
                    return node.data.partitionDetails.code === code;
                })
                .style("opacity", 1)
                .style("fill", this.colors[index]);
        });
    }

    // Fade all but the current sequence.
    private highlightPath(selected: HierarchyNode<Partition>): void {
        const percentage = (100 * selected.data.ratio).toFixed(2);
        const percentageString = percentage + "%";

        this.showedPercent = percentageString;
        this.showedLabel = selected.data.partitionDetails.text ? selected.data.partitionDetails.text.shortLabel : "Text not found";
        this.showedValue = selected.data.simulationValues.amount;
        this.isDetailsVisible = true;

        const sequenceArray = selected.ancestors().reverse();
        sequenceArray.shift();

        // Fade all the segments.
        this.vis.selectAll("path").style("opacity", 0.3);

        // Then highlight only those that are an ancestor of the current segment.
        this.vis.selectAll("path")
            .filter(node => {
                return (sequenceArray.indexOf(node) >= 0);
            })
            .style("opacity", 1);
    }

    // Restore everything to full opacity when moving off the visualization.
    private clearHighlight(): void {
        // Transition each segment to full opacity and then reactivate it.
        this.vis.selectAll("path").style("opacity", 1);

        this.isDetailsVisible = false;
    }

    // Create gradient colors array to apply in chart for each partition
    private setPartsColors(partitions: Partition[], sendColor?: string): void {
        partitions.map((partition, index) => {
            const color = sendColor || this.colors[index];
            this.colorsTree.push({
                code: partition.partitionDetails.code,
                color: color
            });

            if (partition.partitions && partition.partitions.length) {
                this.setPartsColors(partition.partitions, color);
            }
        });
    }
}
